import maplibregl from "maplibre-gl";
import { h, ic, toast } from "../dom.ts";
import { show, goHome } from "../main.ts";
import { style, spinGlobe } from "../map.ts";
import { scopeName, type Catalog } from "../catalog.ts";
import { prefs, savePrefs, fmtDist } from "../prefs.ts";
import type { GameController, RoundResult, Player } from "../game.ts";
import { setupScreen } from "./setup.ts";
import { startSolo } from "./play.ts";
import { MAX_POINTS, type GameConfig, type Place } from "../../../shared/game.ts";

interface ResultsOpts { daily?: boolean; players?: Player[]; me?: string; onPlayAgain?: () => void; isHost?: boolean; onLeave?: () => void }

export function resultsScreen(game: GameController, cfg: GameConfig, cat: Catalog, o: ResultsOpts = {}): HTMLElement {
  spinGlobe(false);
  const res = game.results();
  const total = res.reduce((s, r) => s + r.points + r.bonus, 0);
  const max = res.length * MAX_POINTS;
  const acc = max ? Math.round((res.reduce((s, r) => s + r.points, 0) / max) * 100) : 0;
  const guessed = res.filter(r => r.distanceKm >= 0);
  const avg = guessed.length ? guessed.reduce((s, r) => s + r.distanceKm, 0) / guessed.length : -1;
  const best = guessed.slice().sort((a, b) => a.distanceKm - b.distanceKm)[0];
  let streak = 0, run = 0;
  for (const r of res) { run = r.points >= 750 ? run + 1 : 0; streak = Math.max(streak, run); }

  const mapLabel = scopeName(cfg.scope, cat);
  const bestKey = `best-${JSON.stringify(cfg.scope)}-${cfg.kinds.slice().sort().join(",")}-${cfg.difficulty}-${cfg.rounds}`;
  const prevBest = prefs.best[bestKey];
  const newBest = !game.multiplayer && !o.daily && res.length > 0 && (prevBest == null || total > prevBest);
  if (newBest) { prefs.best[bestKey] = total; savePrefs(prefs); }

  const players = (o.players ?? []).slice().sort((a, b) => (a.rank ?? 1e9) - (b.rank ?? 1e9) || b.score - a.score);
  const meP = players.find(p => p.id === o.me);
  const place = meP?.rank ?? players.findIndex(p => p.id === o.me) + 1;
  const shown = players.filter(p => (p.rank ?? 99) <= 10 || p.id === o.me);
  const title = game.multiplayer
    ? place === 1 ? `You won, ${prefs.name || "friend"}!` : `${ordinal(place)} place, ${prefs.name || "friend"}.`
    : o.daily ? (res.length < 5 ? "Daily challenge ended early." : "Daily challenge done.") : newBest && prevBest != null ? "New personal best!" : acc >= 70 ? "Great game!" : acc >= 40 ? "Nice work." : "Every miss is a new place learned.";

  const colorFor = (p: number) => (p >= 750 ? "var(--lime)" : p >= 300 ? "var(--gold)" : "var(--coral)");
  const cls = (p: number) => (p >= 750 ? "c-good" : p >= 300 ? "c-ok" : "c-miss");
  const worst = guessed.slice().sort((a, b) => b.distanceKm - a.distanceKm).slice(0, 3)
    .concat(res.filter(r => r.distanceKm < 0)).slice(0, 3);

  const mapBox = h("div", { class: "mini-map", role: "img", "aria-label": "Map of your guesses and the answers" });
  requestAnimationFrame(() => drawMiniMap(mapBox, res));

  const share = async () => {
    const squares = res.map(r => (r.points >= 750 ? "🟩" : r.points >= 300 ? "🟨" : "🟥")).join("");
    const text = `GeoPin · ${o.daily ? "Daily challenge" : mapLabel}\n${total.toLocaleString()} / ${max.toLocaleString()}\n${squares}\nplaygeopin.com${o.daily ? "/daily" : ""}`;
    try {
      if (navigator.share) await navigator.share({ text });
      else { await navigator.clipboard.writeText(text); toast("Score copied. Paste it anywhere."); }
    } catch { /* user cancelled */ }
  };

  const actions = game.multiplayer
    ? [h("button", { class: "btn", onclick: share }, ic("share"), "Share score"),
       o.isHost ? h("button", { class: "btn lime", onclick: () => o.onPlayAgain?.() }, "Play again") : h("span", { class: "muted", style: "align-self:center" }, "Waiting for the host to start another game"),
       h("button", { class: "btn", onclick: () => (o.onLeave ? o.onLeave() : goHome()) }, "Leave room")]
    : [h("button", { class: "btn", onclick: share }, ic("share"), "Share score"),
       o.daily ? null : h("button", { class: "btn", onclick: () => show(setupScreen({ mode: "solo", config: cfg, onDone: c => startSolo(c) })) }, "Change map"),
       o.daily ? h("button", { class: "btn lime", onclick: () => goHome() }, "Done") : h("button", { class: "btn lime", autofocus: true, onclick: () => startSolo(cfg) }, "Play again")];

  return h("div", { class: "results" }, h("div", { class: "results-inner" },
    h("div", { class: "results-head" },
      h("div", {}, h("p", {}, `${o.daily ? "Daily challenge" : mapLabel} · ${res.length} rounds · ${cap(cfg.difficulty)}`), h("h1", {}, title)),
      h("div", { class: "results-actions" }, actions)),
    h("div", { class: "tiles" },
      tile("Score", total.toLocaleString(), `of ${max.toLocaleString()}`, "score"),
      tile("Accuracy", `${acc}%`, newBest && prevBest != null ? `Beat your best (${prevBest.toLocaleString()})` : prevBest != null && !game.multiplayer ? `Your best: ${Math.max(prevBest, total).toLocaleString()}` : "share of possible points"),
      tile("Average miss", avg >= 0 ? fmtDist(avg) : "—", `across ${guessed.length} ${guessed.length === 1 ? "pin" : "pins"}`),
      tile("Best pin", best ? fmtDist(best.distanceKm) : "—", best?.name ?? "", "best"),
      tile("Longest streak", String(streak), "rounds scoring 750+")),
    h("div", { class: "results-grid" },
      h("div", { class: "card" },
        h("div", { class: "card-title" }, h("h2", {}, "Every pin you dropped"),
          h("div", { class: "legend" }, h("span", {}, h("i", { class: "dot", style: "background:var(--coral)" }), "Your guess"),
            h("span", {}, h("i", { class: "dot", style: "background:var(--lime)" }), "Answer"))),
        mapBox,
        h("div", { class: "bars", "aria-hidden": "true" }, res.map((r, i) => h("div", {},
          h("i", { style: `height:${Math.max(4, Math.round((r.points / MAX_POINTS) * 70))}px;background:${colorFor(r.points)}` }),
          h("span", {}, String(i + 1)))))),
      h("div", { style: "display:flex;flex-direction:column;gap:16px" },
        players.length ? h("div", { class: "card board" }, h("div", { class: "card-title" }, h("h2", {}, "Final standings")),
          shown.map((p, i) => h("div", { class: `line${p.id === o.me ? " me" : ""}` }, h("b", {}, String(p.rank ?? i + 1)),
            h("span", { class: "avatar sm", style: `background:${p.color}` }, p.name[0]?.toUpperCase() ?? "?"),
            h("span", {}, p.id === o.me ? "You" : p.name), h("span", { class: "s" }, p.score.toLocaleString())))) : null,
        worst.length ? h("div", { class: "card practice" },
          h("div", { class: "card-title" }, h("h2", {}, "Places to practice")),
          h("p", { class: "muted", style: "margin:0 0 12px;font-size:14px" }, "Your widest misses. Play them again until they stick."),
          h("div", { class: "chips", style: "margin-bottom:14px" }, worst.map(r => h("span", {}, `${r.name} · ${r.distanceKm >= 0 ? fmtDist(r.distanceKm) : "no pin"}`))),
          h("button", { class: "btn sky", style: "width:100%", onclick: () => {
            const places = worst.map(r => cat.places.find(p => p.id === r.placeId)).filter((p): p is Place => !!p);
            if (game.multiplayer) o.onLeave?.();
            startSolo({ ...cfg, rounds: places.length, timer: 0 }, places);
          } }, `Practice these ${worst.length}`)) : null)),
    h("div", { class: "card" }, h("div", { class: "card-title" }, h("h2", {}, "Round by round")),
      h("div", { class: "round-list" }, res.map((r, i) => h("div", {},
        h("span", { class: "n" }, String(i + 1)), h("span", { class: "name" }, r.name),
        h("span", { class: "km" }, r.distanceKm >= 0 ? fmtDist(r.distanceKm) : "no pin"),
        h("span", { class: `p ${cls(r.points)}` }, (r.points + r.bonus).toLocaleString())))))));
}

function tile(label: string, value: string, sub: string, cls = "") {
  return h("div", { class: `tile ${cls}` }, h("small", {}, label), h("b", {}, value), h("span", {}, sub));
}
const cap = (s: string) => s[0].toUpperCase() + s.slice(1);
function ordinal(n: number) { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); }

function drawMiniMap(el: HTMLElement, res: RoundResult[]) {
  if (!el.isConnected || !res.length) return;
  const m = new maplibregl.Map({ container: el, style: style(), attributionControl: { compact: true }, interactive: true,
    center: [res[0].answer.lng, res[0].answer.lat], zoom: 2, dragRotate: false });
  m.on("style.load", () => {
    m.addSource("links", { type: "geojson", data: { type: "FeatureCollection", features: res.filter(r => r.guess).map(r => ({
      type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: [[r.guess!.lng, r.guess!.lat], [r.answer.lng, r.answer.lat]] } })) } });
    m.addLayer({ id: "links", type: "line", source: "links", paint: { "line-color": "#0B1B33", "line-width": 1.5, "line-dasharray": [2, 2] } });
  });
  const b = new maplibregl.LngLatBounds();
  for (const r of res) {
    const dot = (color: string) => { const d = document.createElement("div"); d.style.cssText = `width:12px;height:12px;border-radius:50%;background:${color};border:2px solid #0B1B33`; return d; };
    new maplibregl.Marker({ element: dot("#8BD43A") }).setLngLat([r.answer.lng, r.answer.lat]).addTo(m);
    b.extend([r.answer.lng, r.answer.lat]);
    if (r.guess) { new maplibregl.Marker({ element: dot("#FF7A59") }).setLngLat([r.guess.lng, r.guess.lat]).addTo(m); b.extend([r.guess.lng, r.guess.lat]); }
  }
  m.fitBounds(b, { padding: 40, maxZoom: 7, animate: false });
}
